
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

export const useNotificationMutations = (id: string | undefined) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Removing the markAsRead mutation since it was incorrectly changing the status
  // We should not automatically change the status when viewing a notification

  // Define the mutation with proper typing for optional parameters
  const archiveNotification = useMutation<string, Error, void | string>({
    mutationFn: async (notificationId?: string) => {
      const idToUse = notificationId !== undefined ? notificationId : id;
      
      if (!idToUse) throw new Error("Notification ID is required");
      
      const { error } = await supabase
        .from('notifications')
        .update({ archived: true })
        .eq('id', idToUse);
      
      if (error) throw error;
      return idToUse;
    },
    onSuccess: (notificationId) => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      if (id === notificationId) {
        queryClient.invalidateQueries({ queryKey: ['notification', id] });
      }
      toast.success("Melding gearchiveerd");
    },
    onError: (error: any) => {
      toast.error("Fout bij het archiveren van de melding", {
        description: error.message || "Onbekende fout"
      });
    }
  });

  // Define the mutation with proper typing for optional parameters
  const restoreNotification = useMutation<string, Error, void | string>({
    mutationFn: async (notificationId?: string) => {
      const idToUse = notificationId !== undefined ? notificationId : id;
      
      if (!idToUse) throw new Error("Notification ID is required");
      
      const { error } = await supabase
        .from('notifications')
        .update({ archived: false })
        .eq('id', idToUse);
      
      if (error) throw error;
      return idToUse;
    },
    onSuccess: (notificationId) => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      if (id === notificationId) {
        queryClient.invalidateQueries({ queryKey: ['notification', id] });
      }
      toast.success("Melding hersteld");
    },
    onError: (error: any) => {
      toast.error("Fout bij het herstellen van de melding", {
        description: error.message || "Onbekende fout"
      });
    }
  });

  // Updated to implement soft delete
  const deleteNotification = useMutation<string, Error, void | string>({
    mutationFn: async (notificationId?: string) => {
      const idToUse = notificationId !== undefined ? notificationId : id;
      
      if (!idToUse) throw new Error("Notification ID is required");
      
      // Update the notification to mark it as deleted instead of actually deleting it
      const { error } = await supabase
        .from('notifications')
        .update({ 
          is_deleted: true, 
          deleted_at: new Date().toISOString() 
        })
        .eq('id', idToUse);
      
      if (error) throw error;
      return idToUse;
    },
    onSuccess: (notificationId) => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      
      // Only navigate away if we're on the detail page of the deleted notification
      if (id === notificationId) {
        toast.success("Melding verwijderd");
        navigate('/notifications');
      } else {
        toast.success("Melding verwijderd");
      }
    },
    onError: (error: any) => {
      toast.error("Fout bij het verwijderen van de melding", {
        description: error.message || "Onbekende fout"
      });
    }
  });

  return {
    // Removed markAsRead from the return object
    archiveNotification,
    restoreNotification,
    deleteNotification,
    user
  };
};
